import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { Building2, ChevronsUpDown, Eye, LogOut, PauseCircle, UserCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useToast } from '@/components/ui/toast'
import { ROLE_LABELS, useAuth } from '@/lib/auth'
import { api } from '@/lib/http'

/** 契約プロジェクトの切り替え。運営はすべての契約を選べる（中は読むだけ）。 */
export function ProjectSwitcher() {
  const { me, switchProject } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const isOperator = Boolean(me?.user?.isOperator)
  const all = useQuery({
    queryKey: ['ops', 'projects', 'switcher'],
    queryFn: ({ signal }) => api.get<{ projects: Array<{ id: string; name: string; status: string }> }>('/ops/projects', undefined, signal),
    enabled: isOperator && open,
  })

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const active = me?.activeProject
  const mine = me?.projects ?? []
  const others = isOperator ? (all.data?.projects ?? []).filter((p) => !mine.some((m) => m.id === p.id)) : []
  if (!active && !mine.length && !isOperator) return null

  async function pick(id: string) {
    setOpen(false)
    try {
      await switchProject(id)
      navigate('/dashboard')
    } catch (e) {
      toast.error('切り替えられませんでした', e instanceof Error ? e.message : undefined)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-2 text-left transition-colors hover:border-line-strong"
      >
        <Building2 className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-ink">{active?.name ?? '契約を選ぶ'}</span>
          {active ? <span className="block truncate text-2xs text-ink-3">{ROLE_LABELS[active.role]}</span> : null}
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
      </button>
      {open ? (
        <div role="listbox" className="absolute inset-x-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border border-line bg-surface p-1 shadow-pop">
          {mine.map((p) => (
            <button key={p.id} type="button" role="option" aria-selected={p.id === active?.id} onClick={() => void pick(p.id)} className={clsx('flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-surface-hover', p.id === active?.id && 'bg-accent/10 font-semibold text-accent')}>
              <span className="truncate">{p.name}</span>
              <span className="shrink-0 text-2xs text-ink-3">{ROLE_LABELS[p.role]}</span>
            </button>
          ))}
          {isOperator ? (
            <>
              <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-3">運営として見る</p>
              {all.isLoading ? <p className="px-2 py-1.5 text-2xs text-ink-3">読み込み中…</p> : null}
              {others.map((p) => (
                <button key={p.id} type="button" role="option" aria-selected={p.id === active?.id} onClick={() => void pick(p.id)} className={clsx('flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-surface-hover', p.id === active?.id && 'bg-accent/10 font-semibold text-accent')}>
                  <span className="truncate">{p.name}</span>
                  {p.status === 'suspended' ? <span className="shrink-0 text-2xs text-negative">停止中</span> : null}
                </button>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** 自分（名前・自分の設定・ログアウト）。 */
export function UserMenu() {
  const { me, logout } = useAuth()
  if (!me?.user) return null
  return (
    <div className="flex items-center gap-2">
      <Link to="/account" className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-1 hover:bg-surface-hover">
        <UserCircle className="h-5 w-5 shrink-0 text-ink-3" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium text-ink">{me.user.name}</span>
          <span className="block truncate text-2xs text-ink-3">{me.user.isOperator ? '運営' : me.user.email}</span>
        </span>
      </Link>
      <button type="button" onClick={() => void logout()} title="ログアウト" aria-label="ログアウト" className="grid h-8 w-8 place-items-center rounded-lg text-ink-3 hover:bg-surface-hover hover:text-ink">
        <LogOut className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}

/** 画面の上のお知らせ（運営として閲覧中・契約が停止中・自動ルールが止まっている）。 */
export function ShellBanners() {
  const { me } = useAuth()
  const active = me?.activeProject
  const auto = useQuery({
    queryKey: ['rules', 'automation-banner', active?.id],
    queryFn: ({ signal }) => api.get<{ automation: { paused: boolean; reason: string | null; by: string | null } }>('/rules', undefined, signal),
    enabled: Boolean(active),
    staleTime: 60_000,
  })
  if (!active) return null
  return (
    <div className="mb-3 space-y-2 empty:hidden">
      {active.role === 'ops_view' ? (
        <p className="flex items-center gap-2 rounded-lg border border-info/30 bg-info/10 px-3 py-2 text-xs text-info">
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
          運営として「{active.name}」を閲覧しています。中の数字を読むだけで、操作はできません。
        </p>
      ) : null}
      {active.status === 'suspended' ? (
        <p className="flex items-center gap-2 rounded-lg border border-negative/30 bg-negative/10 px-3 py-2 text-xs text-negative">
          <PauseCircle className="h-4 w-4 shrink-0" aria-hidden />
          この契約は停止中です。取り込みと自動ルールは止まっています。
        </p>
      ) : null}
      {auto.data?.automation.paused ? (
        <p className="flex items-center gap-2 rounded-lg border border-caution/30 bg-caution/10 px-3 py-2 text-xs text-caution">
          <PauseCircle className="h-4 w-4 shrink-0" aria-hidden />
          自動ルールを全体で止めています{auto.data.automation.reason ? `（${auto.data.automation.reason}）` : ''}。
          <Link to="/rules" className="underline underline-offset-2">
            自動ルールへ
          </Link>
        </p>
      ) : null}
    </div>
  )
}
